// ── MONEY MATH ────────────────────────────────────────────────────────────
// Pure financial calculation functions — the single source of truth for the
// numbers users trust: WAC unit cost, sale totals, profit, COGS, and credit
// balances. App.js delegates to these, and moneyMath.test.js locks each one
// down with known-answer tests so future changes can't silently corrupt
// profit figures. No React, no localStorage, no side effects here.

export const normProductName = (s) => (s || '').trim().toLowerCase().replace(/\s+/g, ' ');

// A purchase may be a legacy flat record or carry a multi-item array.
export const purchaseLineItems = (purchase) =>
  Array.isArray(purchase.items) && purchase.items.length > 0
    ? purchase.items
    : [{
        productName: purchase.productName,
        quantity: purchase.quantity,
        totalCost: purchase.totalCost,
        unitPrice: purchase.unitPrice || purchase.costPrice,
      }];

// ── Weighted Average Cost (WAC) — PERPETUAL MOVING AVERAGE ─────────────────
// The average is recalculated on every purchase (weighted against the stock
// currently on hand); sales consume at the current average without changing
// it. This is the standard inventory-costing method and is more accurate than
// a lifetime "all purchases ever" average once stock turns over and prices
// move. It stays edit-safe by replaying the whole event history in date order
// each time (no stored cost layers to corrupt).
// Falls back to the product's stored costPrice when there is no history.
export const computeWAC = (productName, products = [], purchases = [], sales = [], salesReturns = [], purchaseReturns = [], targetProduct = null, asOf = null) => {
  const tgt = normProductName(productName);
  // `asOf` (a 'YYYY-MM-DD' date) caps the replay so a sale-time cost snapshot
  // reflects WAC AS OF the sale date — a future-dated purchase never inflates
  // today's cost. Compared as strings (timezone-independent), like the ledger.
  const asOfKey = asOf ? String(asOf).slice(0, 10) : null;
  // Resolve the target product so we can match movements by STABLE productId
  // (falling back to normalized name for legacy lines). `targetProduct` lets a
  // caller pin the exact product when two share a name.
  const prod = targetProduct || (products || []).find((pp) => normProductName(pp.productName) === tgt);
  const pid = prod && prod.id != null && prod.id !== '' ? String(prod.id) : null;
  // SAME identity rule the stock replay uses (inventory.js lineMatchesProduct):
  // productId first, normalized name fallback.
  const m = (it) => {
    if (pid != null && it && it.productId != null && it.productId !== '') return String(it.productId) === pid;
    return normProductName(it && it.productName) === tgt;
  };

  const events = [];
  (purchases || []).forEach((pu) => purchaseLineItems(pu).forEach((it) => {
    if (!m(it)) return;
    const q = parseFloat(it.quantity || 0);
    const c = parseFloat(it.totalCost || (q * parseFloat(it.unitPrice || it.unitCost || 0))) || 0;
    if (q > 0) events.push({ date: pu.date, t: 0, kind: 'in', qty: q, cost: c });
  }));
  (sales || []).forEach((s) => {
    const its = Array.isArray(s.items) && s.items.length > 0 ? s.items : [{ productName: s.productName, quantity: s.quantity, productId: s.productId, type: s.isService ? 'service' : 'product' }];
    its.forEach((it) => {
      if (it.type === 'service') return;
      if (!m(it)) return;
      const q = parseFloat(it.quantity || 0);
      if (q > 0) events.push({ date: s.date, t: 1, kind: 'out', qty: q });
    });
  });
  (salesReturns || []).forEach((r) => {
    const its = Array.isArray(r.items) && r.items.length > 0 ? r.items : [{ productName: r.productName, qty: r.qty, productId: r.productId, unitCost: r.unitCost }];
    its.forEach((it) => {
      if (!m(it)) return;
      const q = parseFloat(it.qty || it.quantity || 0);
      // Customer return goes BACK into stock at the ORIGINAL sale cost snapshot
      // (matching the COGS reversal), not the drifted current WAC. `unitCost`
      // is the snapshot; when absent (legacy), fall back to current WAC.
      const snap = it.unitCost != null && it.unitCost !== '' ? parseFloat(it.unitCost) : null;
      if (q > 0) events.push({ date: r.date, t: 2, kind: 'in_at_cost', qty: q, cost: Number.isFinite(snap) ? snap : null });
    });
  });
  (purchaseReturns || []).forEach((r) => {
    const its = Array.isArray(r.items) && r.items.length > 0 ? r.items : [{ productName: r.productName, qty: r.qty, productId: r.productId }];
    its.forEach((it) => {
      if (!m(it)) return;
      const q = parseFloat(it.qty || it.quantity || 0);
      if (q > 0) events.push({ date: r.date, t: 3, kind: 'out', qty: q }); // supplier return: leaves stock at current cost
    });
  });
  events.sort((a, b) => (new Date(a.date || 0) - new Date(b.date || 0)) || (a.t - b.t));
  // As-of cap: drop events dated AFTER the snapshot date.
  const replayEvents = asOfKey ? events.filter((e) => String(e.date || '').slice(0, 10) <= asOfKey) : events;

  let qty = parseFloat(prod?.openingStockQty || 0) || 0;
  let value = parseFloat(prod?.openingStockValue || 0) || 0;
  let lastWAC = qty > 0 ? value / qty : (parseFloat(prod?.costPrice || 0) || 0);
  for (const e of replayEvents) {
    const wac = qty > 0 ? value / qty : lastWAC;
    if (e.kind === 'in') { value += e.cost; qty += e.qty; }
    else if (e.kind === 'in_at_cost') { value += e.qty * (e.cost != null ? e.cost : wac); qty += e.qty; }
    else { const out = Math.min(e.qty, qty); value -= out * wac; qty -= out; } // 'out'
    if (qty < 0) qty = 0;
    if (value < 0) value = 0;
    if (qty > 0) lastWAC = value / qty;
  }
  if (qty > 0 && value > 0) return value / qty;
  return lastWAC || (parseFloat(prod?.costPrice || 0) || 0);
};

// ── Chronological inventory replay (COGS from the SAME replay as valuation) ─
// Returns { openingValue, purchasesValue, cogs, closingQty, closingValue } for
// one product, so the identity ALWAYS holds:
//   openingValue + purchasesValue − cogs (+returns net) = closingValue
// This is the accounting-consistent alternative to frozen sale-time COGS
// snapshots, which can diverge from valuation when history is entered out of
// order. `asOf` caps the replay to a date; matches by productId (name fallback).
export const replayInventory = (productName, { products = [], purchases = [], sales = [], salesReturns = [], purchaseReturns = [] } = {}, asOf = null, periodFrom = null) => {
  const tgt = normProductName(productName);
  const prod = (products || []).find((pp) => normProductName(pp.productName) === tgt);
  const pid = prod && prod.id != null && prod.id !== '' ? String(prod.id) : null;
  const asOfKey = asOf ? String(asOf).slice(0, 10) : null;
  const m = (it) => (pid != null && it && it.productId != null && it.productId !== '') ? String(it.productId) === pid : normProductName(it && it.productName) === tgt;
  const ev = [];
  const num2 = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };
  (purchases || []).forEach((pu) => purchaseLineItems(pu).forEach((it) => { if (!m(it)) return; const q = num2(it.quantity); const c = num2(it.totalCost || q * num2(it.unitPrice || it.unitCost)); if (q > 0) ev.push({ date: pu.date, t: 0, kind: 'in', qty: q, cost: c }); }));
  (sales || []).forEach((s) => { const its = Array.isArray(s.items) && s.items.length ? s.items : [{ productName: s.productName, quantity: s.quantity, productId: s.productId, type: s.isService ? 'service' : 'product' }]; its.forEach((it) => { if (it.type === 'service' || !m(it)) return; const q = num2(it.quantity); if (q > 0) ev.push({ date: s.date, t: 1, kind: 'out', qty: q }); }); });
  (salesReturns || []).forEach((r) => { const its = Array.isArray(r.items) && r.items.length ? r.items : [{ productName: r.productName, qty: r.qty, productId: r.productId, unitCost: r.unitCost }]; its.forEach((it) => { if (!m(it)) return; const q = num2(it.qty != null ? it.qty : it.quantity); const snap = it.unitCost != null && it.unitCost !== '' ? num2(it.unitCost) : null; if (q > 0) ev.push({ date: r.date, t: 2, kind: 'in_at_cost', qty: q, cost: snap }); }); });
  (purchaseReturns || []).forEach((r) => { const its = Array.isArray(r.items) && r.items.length ? r.items : [{ productName: r.productName, qty: r.qty, productId: r.productId }]; its.forEach((it) => { if (!m(it)) return; const q = num2(it.qty != null ? it.qty : it.quantity); if (q > 0) ev.push({ date: r.date, t: 3, kind: 'out', qty: q }); }); });
  ev.sort((a, b) => (new Date(a.date || 0) - new Date(b.date || 0)) || (a.t - b.t));
  const events = asOfKey ? ev.filter((e) => String(e.date || '').slice(0, 10) <= asOfKey) : ev;
  const openingQty = num2(prod && prod.openingStockQty);
  const openingValue = num2(prod && prod.openingStockValue);
  let qty = openingQty, value = openingValue, lastWAC = qty > 0 ? value / qty : num2(prod && prod.costPrice);
  const fromKey = periodFrom ? String(periodFrom).slice(0, 10) : null;
  const inPeriod = (e) => !fromKey || String(e.date || '').slice(0, 10) >= fromKey; // COGS accrues only for in-period sale/return events (WAC still replays ALL prior history)
  let purchasesValue = 0, cogs = 0;
  for (const e of events) {
    const wac = qty > 0 ? value / qty : lastWAC;
    if (e.kind === 'in') { value += e.cost; qty += e.qty; purchasesValue += e.cost; }
    else if (e.kind === 'in_at_cost') { const c = e.cost != null ? e.cost : wac; value += e.qty * c; qty += e.qty; if (inPeriod(e)) cogs -= e.qty * c; } // customer return reverses COGS
    else { const out = Math.min(e.qty, qty); const c = out * wac; value -= c; qty -= out; if (e.t === 1) { if (inPeriod(e)) cogs += c; } else purchasesValue -= c; } // sale → COGS; supplier return → less purchases
    if (qty > 0) lastWAC = value / qty;
  }
  return { openingValue, purchasesValue, cogs, closingQty: qty, closingValue: value };
};

// Period COGS across ALL products, derived from the chronological replay. Prior
// history is always replayed (for correct WAC); only in-period sale/return COGS
// is accumulated. This is the accounting-correct period COGS to power the P&L.
export const periodReplayCOGS = (inputs = {}, from = null, to = null) =>
  (inputs.products || []).reduce((sum, p) => sum + replayInventory(p.productName, inputs, to, from).cogs, 0);

// Developer comparison (#10): stored frozen-snapshot COGS vs replay-derived COGS
// for a period, so the operator can SEE the impact before any report is switched.
export const cogsSnapshotVsReplay = (inputs = {}, from = null, to = null) => {
  const inRange = (d) => { const k = String(d || '').slice(0, 10); return (!from || k >= from) && (!to || k <= to); };
  const snapshotCOGS = (inputs.sales || [])
    .filter((s) => !s.deleted && inRange(s.date))
    .reduce((s, x) => s + (parseFloat(x.totalCost || 0) || 0), 0);
  const replayCOGS = periodReplayCOGS(inputs, from, to);
  return { snapshotCOGS, replayCOGS, difference: Math.round((snapshotCOGS - replayCOGS) * 1e6) / 1e6 };
};

// Per-product COGS + inventory breakdown (Part C diagnostic). For each product it
// shows opening value, in-period purchases, the frozen per-line snapshot COGS
// (Σ qty×line.cost) and the chronological replay COGS, plus their difference and
// closing position. Summing the rows localises exactly which products drive a
// snapshot-vs-replay gap, so the operator can judge whether replay is trustworthy
// BEFORE any displayed report is switched. Ambiguous rows (a normalized name
// shared by >1 product) are flagged — never silently attributed. Pure.
export const perProductCOGSBreakdown = (inputs = {}, from = null, to = null) => {
  const { products = [], sales = [] } = inputs;
  const fromKey = from ? String(from).slice(0, 10) : null;
  const toKey = to ? String(to).slice(0, 10) : null;
  const inRange = (d) => { const k = String(d || '').slice(0, 10); return (!fromKey || k >= fromKey) && (!toKey || k <= toKey); };
  const n = (v) => { const x = parseFloat(v); return Number.isFinite(x) ? x : 0; };
  const r6 = (v) => Math.round(v * 1e6) / 1e6;
  const nameCounts = {};
  (products || []).forEach((p) => { const k = normProductName(p.productName); nameCounts[k] = (nameCounts[k] || 0) + 1; });
  const rangeSales = (sales || []).filter((s) => !s.deleted && inRange(s.date));
  return (products || []).map((p) => {
    const tgt = normProductName(p.productName);
    const pid = p.id != null && p.id !== '' ? String(p.id) : null;
    const m = (it) => (pid != null && it && it.productId != null && it.productId !== '') ? String(it.productId) === pid : normProductName(it && it.productName) === tgt;
    let snapshotCOGS = 0;
    rangeSales.forEach((s) => {
      const its = Array.isArray(s.items) && s.items.length ? s.items : [{ productName: s.productName, quantity: s.quantity, productId: s.productId, cost: s.cost, type: s.isService ? 'service' : 'product' }];
      its.forEach((it) => { if (it.type === 'service' || !m(it)) return; snapshotCOGS += n(it.quantity) * n(it.cost); });
    });
    const rep = replayInventory(p.productName, inputs, toKey, fromKey);
    return {
      productId: p.id, productName: p.productName,
      ambiguous: nameCounts[tgt] > 1,
      openingValue: r6(rep.openingValue), purchasesValue: r6(rep.purchasesValue),
      snapshotCOGS: r6(snapshotCOGS), replayCOGS: r6(rep.cogs),
      difference: r6(snapshotCOGS - rep.cogs),
      closingQty: r6(rep.closingQty), closingValue: r6(rep.closingValue),
    };
  }).sort((a, b) => Math.abs(b.difference) - Math.abs(a.difference));
};

// ── Sale order totals ─────────────────────────────────────────────────────
// Mirrors the Record Sale flow exactly:
//   items: [{ type: 'product'|'service', quantity, sellingPrice, cost }]
//   discountPct: order-level % discount applied on the subtotal
//   taxes: [{ rate }] — each applied on the DISCOUNTED subtotal
// Services count once (no quantity) and their cost is taken as-is.
export const computeSaleTotals = (items = [], discountPct = 0, taxes = []) => {
  let baseRevenue = 0;
  let totalCost = 0;
  (items || []).forEach((item) => {
    const qty = item.type === 'service' ? 1 : parseFloat(item.quantity || 1);
    const price = parseFloat(item.sellingPrice || 0);
    const cost = parseFloat(item.cost || 0);
    baseRevenue += item.type === 'service' ? price : qty * price;
    totalCost += item.type === 'service' ? cost : qty * cost;
  });
  const discPct = parseFloat(discountPct || 0);
  const discountAmount = baseRevenue * (discPct / 100);
  const afterDiscount = Math.max(0, baseRevenue - discountAmount);
  const taxTotal = (taxes || []).reduce(
    (sum, tax) => sum + afterDiscount * (parseFloat(tax.rate || 0) / 100),
    0
  );
  const totalRevenue = Math.max(0, afterDiscount + taxTotal);
  const profit = totalRevenue - totalCost;
  return { baseRevenue, totalCost, discountAmount, afterDiscount, taxTotal, totalRevenue, profit };
};

// ── Credit / outstanding balance ──────────────────────────────────────────
// Credit is NOT money received — only real payments (cash/bank/mobile money)
// reduce what the customer owes.
export const outstandingBalance = (totalRevenue, payments = []) => {
  const paid = (payments || [])
    .filter((p) => p && p.type !== 'credit' && parseFloat(p.amount || 0) > 0)
    .reduce((s, p) => s + parseFloat(p.amount), 0);
  return Math.max(0, (parseFloat(totalRevenue) || 0) - paid);
};

// ── Period COGS ───────────────────────────────────────────────────────────
// COGS for a period = sum of the cost snapshots stored on each sale record.
export const periodCOGS = (sales = []) =>
  (sales || []).reduce((s, x) => s + parseFloat(x.totalCost || 0), 0);

// ── Net sales after returns ───────────────────────────────────────────────
export const netSales = (totalSales, returnsTotal) =>
  (parseFloat(totalSales) || 0) - Math.abs(parseFloat(returnsTotal) || 0);

// ── VAT amount (output on sales, input on purchases) ──────────────────────
// CRITICAL: VAT is computed from the tax BASE (the discounted, PRE-tax
// subtotal), NEVER from the VAT-inclusive grand total. Computing
// `grandTotal × rate` overstates VAT by rate% of the VAT itself (e.g. a
// 1,180,000 inclusive total × 18% = 212,400 instead of the correct 180,000).
// Only 'vat'-type taxes count, so a withholding/other levy on the same record
// never inflates the VAT figure.
//   • item-based record:  base × rate/100                    (exact)
//   • legacy record:      inclusiveTotal × rate/(100+rate)   (backs VAT out)
// The two are algebraically identical when VAT is the only tax; the base
// method stays exact even when other taxes are also present on the record.
const taxRateOfType = (taxes = [], type) =>
  (taxes || [])
    .filter((t) => t && t.type === type)
    .reduce((r, t) => r + (parseFloat(t.rate || 0) || 0), 0);

// Amount of a given tax TYPE ('vat' | 'withholding' | …) on a sale, from the
// pre-tax base. Generic so VAT and WHT reports share one correct definition.
export const saleTaxOfType = (sale, type) => {
  const rate = taxRateOfType(sale && sale.taxes, type);
  if (rate <= 0) return 0;
  const items = Array.isArray(sale && sale.items) && sale.items.length > 0 ? sale.items : null;
  if (items) {
    const subTotal = items.reduce((s, it) => {
      const qty = it.type === 'service' ? (parseFloat(it.quantity || 1) || 1) : parseFloat(it.quantity || 0);
      // Service line items use `price`; product lines use `sellingPrice`.
      const unit = parseFloat(it.sellingPrice != null && it.sellingPrice !== '' ? it.sellingPrice : (it.price || 0));
      return s + qty * unit;
    }, 0);
    const disc = parseFloat((sale && sale.discount) || 0);
    const base = Math.max(0, subTotal - subTotal * (disc / 100));
    return base * (rate / 100);
  }
  // Legacy record with no line items: back the tax out of the inclusive total.
  const total = parseFloat((sale && sale.totalRevenue) || 0);
  return total > 0 ? total * (rate / (100 + rate)) : 0;
};

export const purchaseTaxOfType = (purchase, type) => {
  const rate = taxRateOfType(purchase && purchase.taxes, type);
  if (rate <= 0) return 0;
  const items = Array.isArray(purchase && purchase.items) && purchase.items.length > 0 ? purchase.items : null;
  if (items) {
    const subTotal = items.reduce((s, it) => s + parseFloat(it.totalCost || 0), 0);
    const disc = parseFloat((purchase && purchase.discount) || 0);
    const base = Math.max(0, subTotal - subTotal * (disc / 100));
    return base * (rate / 100);
  }
  const total = parseFloat((purchase && purchase.totalCost) || 0);
  return total > 0 ? total * (rate / (100 + rate)) : 0;
};

// VAT is the 'vat'-typed tax; thin wrappers over the generic helper.
export const saleVAT = (sale) => saleTaxOfType(sale, 'vat');
export const purchaseVAT = (purchase) => purchaseTaxOfType(purchase, 'vat');

// Net VAT payable to (or refundable from) the tax authority for a set of
// records: output VAT collected on sales minus input VAT paid on purchases.
export const netVATPayable = (sales = [], purchases = []) =>
  (sales || []).reduce((s, x) => s + saleVAT(x), 0) -
  (purchases || []).reduce((s, x) => s + purchaseVAT(x), 0);

// ── Profit EXCLUDING output VAT (single source for every profit surface) ──
// Output VAT is a TRA liability, never income — so profit must be computed on
// the ex-VAT revenue. Works for a product SALE or a SERVICE transaction alike:
// back the VAT out of the stored (VAT-inclusive) total by the record's vat rate.
//   netRevenueExVAT = total × 100/(100+rate)   (rate=0 → net = total)
//   grossProfitExVAT = netRevenueExVAT − COGS/serviceCost snapshot
// Callers must use these instead of summing the stored VAT-inclusive `profit`.
export const netRevenueExVAT = (rec) => {
  const total = parseFloat((rec && rec.totalRevenue) || 0);
  if (total <= 0) return 0;
  // Hierarchy (robust to multi-tax / withholding / discounts):
  //   1. subtract the COMPUTED VAT amount (from the discounted base, VAT-type
  //      only) — never a gross÷(1+rate) back-out that only holds when the whole
  //      gross is one VAT rate.
  //   2. saleTaxOfType already falls back to an inclusive back-out for legacy
  //      records that have no line items.
  const vat = saleTaxOfType(rec, 'vat');
  return Math.max(0, total - vat);
};
export const grossProfitExVAT = (rec) => {
  const cost = parseFloat((rec && rec.totalCost) || 0);
  return netRevenueExVAT(rec) - (Number.isFinite(cost) ? cost : 0);
};
// Period aggregates — sum of per-record ex-VAT gross profit.
export const periodProfitExVAT = (records = []) =>
  (records || []).reduce((s, r) => s + grossProfitExVAT(r), 0);
